import type { RelationPermissionsReturn } from '#layers/forms/types/permissions';
import type { UseRelationPermissionsOptions } from './use-relation-permissions-base';
import { computed } from 'vue';
import { getRelationConstraint } from '#layers/data-model/public';
import { useRelationPermissionsBase } from './use-relation-permissions-base';

export function useM2OPermissions(options: UseRelationPermissionsOptions): RelationPermissionsReturn {
	const {
		permissionsStore,
		field,
		currentColId,
		oppositeColId,
		isCreateForm,
		localConstrainedFieldIds,
		oppositeConstrainedFieldIds,
		relationAllows,
		useRelatedWriteContexts,
	} = useRelationPermissionsBase(options);
	const isOppositeSideConstrained = computed(() =>
		field.value ? getRelationConstraint(field.value).oppositeSideConstrained : false,
	);

	// Computed: check local FK write permission based on form mode
	const canWriteLocalFk = computed(() => {
		if (!currentColId.value) return false;
		return isCreateForm.value
			? permissionsStore.canCreateFields(currentColId.value, localConstrainedFieldIds.value)
			: permissionsStore.canUpdateFields(currentColId.value, localConstrainedFieldIds.value);
	});
	const canUpdateOppositeFk = computed(() => {
		if (!oppositeColId.value) return false;
		return permissionsStore.canUpdate(oppositeColId.value)
			&& permissionsStore.canUpdateFields(oppositeColId.value, oppositeConstrainedFieldIds.value);
	});
	const canCreateOppositeFk = computed(() => {
		if (!oppositeColId.value) return false;
		return permissionsStore.canCreateFields(oppositeColId.value, oppositeConstrainedFieldIds.value);
	});
	const canWriteRelation = computed(() =>
		isOppositeSideConstrained.value ? canUpdateOppositeFk.value : canWriteLocalFk.value,
	);

	// To-one relations may be forward (FK on this item) or inverse (FK on the opposite item).
	const canConnect = computed(() => {
		if (!oppositeColId.value) return false;
		if (!relationAllows((relation) => relation.connect())) return false;
		return permissionsStore.canRead(oppositeColId.value) && canWriteRelation.value;
	});

	const canCreate = computed(() => {
		if (!oppositeColId.value) return false;
		if (!relationAllows((relation) => relation.create() !== undefined)) return false;
		return permissionsStore.canCreate(oppositeColId.value)
			&& (isOppositeSideConstrained.value ? canCreateOppositeFk.value : canWriteLocalFk.value);
	});

	const canDisconnect = computed(() => {
		if (!oppositeColId.value) return false;
		// On a create form, disconnecting only clears the staged value —
		// no wire operation is involved.
		if (!isCreateForm.value && !relationAllows((relation) => relation.disconnect())) return false;
		// Disconnect nulls the FK - needs same permission as connect/create
		return canWriteRelation.value;
	});

	return { canConnect, canCreate, canDisconnect, ...useRelatedWriteContexts(canCreate) };
}
